import { useEffect, useMemo, useRef, useState } from 'react';
import type { Room, Vec2 } from '../../core/hp/types';
import type { CameraPose } from '../../render/three/Viewer';
import { roomTypeName } from '../../render/plan/PlanView';
import { levelLabel, store } from '../../state/store';
import { useStore } from '../useStore';
import { Icon } from '../Icon';
import { FloorSelector } from '../FloorSelector';
import { containsPoint } from '../../state/sceneGesture';
import { t } from '../../i18n/I18n';
import {
  MINIMAP_ACTIVE_COLOR, MINIMAP_BORDER_COLOR, MINIMAP_HIDDEN_BORDER_COLOR, MINIMAP_MIN_ROOMS, MINIMAP_ROOM_COLOR,
  activeRoom, indicatorAngle, indicatorPoint, labelCenter, minimapLayout, minimapRooms, toMinimap, type MinimapLayout,
} from '../../state/minimap';

/** What the minimap needs from the 3D view; functions, so a not-yet-mounted viewer is fine. */
export interface MinimapSceneAccess {
  pose(): CameraPose | null;
  onFrame(listener: () => void): () => void;
  navigate(room: Room): void;
}

/** The camera pose, refreshed after every drawn frame (and once a second, in case the viewer mounted late). */
function usePose(access: MinimapSceneAccess, enabled: boolean): CameraPose | null {
  const [pose, setPose] = useState<CameraPose | null>(() => (enabled ? access.pose() : null));
  useEffect(() => {
    if (!enabled) { setPose(null); return; }
    let frame = 0;
    const refresh = () => { if (frame) return; frame = requestAnimationFrame(() => { frame = 0; setPose(access.pose()); }); };
    refresh();
    const unsubscribe = access.onFrame(refresh);
    const timer = setInterval(refresh, 1000);
    return () => { unsubscribe(); clearInterval(timer); cancelAnimationFrame(frame); };
  }, [access, enabled]);
  return pose;
}

const pathOf = (room: Room, layout: MinimapLayout): string =>
  room.polygon.map((p, i) => { const s = toMinimap(p, layout); return `${i ? 'L' : 'M'}${s.x.toFixed(1)} ${s.y.toFixed(1)}`; }).join(' ') + ' Z';

/** The room the view is in: the walk camera's own room, or the room an orbit looks at. */
function viewedRoom(rooms: Room[], pose: CameraPose | null): Room | null {
  return activeRoom(rooms, pose ? (pose.mode === 'walk' ? pose.position : pose.target) : null);
}

/** The "I am here" arrow: the camera clamped into the active room, as the app does. */
function marker(pose: CameraPose | null, room: Room | null): { at: Vec2; angle: number } | null {
  if (!pose || !room) return null;
  const anchor = pose.mode !== 'walk' && containsPoint(room.polygon, pose.target) ? pose.target : labelCenter(room.polygon);
  return { at: indicatorPoint(pose.position, room, anchor), angle: indicatorAngle(pose.forward) };
}

function CameraIndicator({ arrow, layout, size }: { arrow: NonNullable<ReturnType<typeof marker>>; layout: MinimapLayout; size: number }) {
  const p = toMinimap(arrow.at, layout);
  // The native bitmap's cone faces 201° clockwise from up; its position dot is at (32, 32).
  return <g className="minimap-indicator" transform={`translate(${p.x.toFixed(1)} ${p.y.toFixed(1)}) rotate(${(arrow.angle - 201).toFixed(1)})`}>
    <image href="/hp/ui/minimap-camera.png" x={-size / 2} y={-size / 2} width={size} height={size} />
  </g>;
}

const roomName = (room: Room): string => room.name || roomTypeName(room.roomType);

/** The toolbar button: the level's rooms as a small icon with the live arrow (the app's MinimapButton). */
export function MinimapButton({ access, planMode }: { access: MinimapSceneAccess; planMode: boolean }) {
  const level = useStore((s) => s.scene?.levels[s.activeLevelIndex]);
  const expanded = useStore((s) => s.showMinimap);
  const rooms = useMemo(() => minimapRooms(level), [level]);
  const pose = usePose(access, !planMode && rooms.length >= MINIMAP_MIN_ROOMS);
  if (rooms.length < MINIMAP_MIN_ROOMS) return null;
  const layout = minimapLayout(rooms, 40, 40, 3);
  const active = viewedRoom(rooms, pose);
  const arrow = marker(pose, active);
  return (
    <button className="minimap-btn glass" aria-label={t('#Minimap')} data-tip={t('#WebTipFloorMap')} data-tip-key="minimap" data-tip-side="above" aria-haspopup="dialog" aria-expanded={expanded} aria-controls="minimap-popup" onClick={() => store.set({ showMinimap: true })}>
      <svg viewBox={`0 0 ${layout.width} ${layout.height}`} aria-hidden="true">
        {rooms.map((room) => <path key={room.index} d={pathOf(room, layout)} fill={room === active ? MINIMAP_ACTIVE_COLOR : MINIMAP_ROOM_COLOR} stroke={MINIMAP_BORDER_COLOR} strokeWidth={1} strokeLinejoin="round" />)}
        {arrow && <CameraIndicator arrow={arrow} layout={layout} size={17} />}
      </svg>
    </button>
  );
}

/** The floor plan popup: tap a room to go there, hide the other rooms, switch the level (the app's MinimapPopup). */
export function MinimapPopup({ access, planMode }: { access: MinimapSceneAccess; planMode: boolean }) {
  const state = useStore((s) => ({ scene: s.scene, projectId: s.projectId, levelIndex: s.activeLevelIndex, isolatedRoom: s.isolatedRoom }));
  const level = state.scene?.levels[state.levelIndex];
  const rooms = useMemo(() => minimapRooms(level), [level]);
  const pose = usePose(access, !planMode);
  const popup = useRef<HTMLElement>(null);
  const [box, setBox] = useState(() => ({ w: Math.min(500, window.innerWidth - 40), h: Math.min(440, Math.round(window.innerHeight * 0.55)) }));
  useEffect(() => {
    const onResize = () => setBox({ w: Math.min(500, window.innerWidth - 40), h: Math.min(440, Math.round(window.innerHeight * 0.55)) });
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    popup.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      // An open menu inside the popup (the floor menu) takes the Escape first; the popup goes on the next one.
      if (event.key === 'Escape' && popup.current?.querySelector('[aria-haspopup][aria-expanded=true]')) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); store.set({ showMinimap: false }); }
      if (event.key !== 'Tab') return;
      const controls = popup.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select, [tabindex="0"]');
      if (!controls?.length) return;
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('resize', onResize); window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('resize', onResize); window.removeEventListener('keydown', onKey, true);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);
  const layout = minimapLayout(rooms, box.w, box.h, 16);
  const active = viewedRoom(rooms, pose);
  const arrow = marker(pose, active);
  const wallStroke = Math.max(2, (level?.walls[0]?.thickness ?? 0.15) * layout.scale * 0.5);
  const isolated = state.isolatedRoom !== null;
  const close = () => store.set({ showMinimap: false });

  const go = (room: Room) => {
    if (isolated) store.set({ isolatedRoom: room.index });
    if (planMode) {
      const xs = room.polygon.map((p) => p.x), zs = room.polygon.map((p) => p.z);
      const extent = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...zs) - Math.min(...zs)) * 1.3;
      window.dispatchEvent(new CustomEvent('hp:pan-plan', { detail: { centre: labelCenter(room.polygon), extent } }));
    } else access.navigate(room);
    close();
  };
  const toggleIsolation = () => store.set({ isolatedRoom: isolated ? null : (active ?? rooms[0])?.index ?? null });

  if (!level) return null;
  const levels = state.scene?.levels ?? [];
  return (
    <div className="modal-backdrop minimap-backdrop" onClick={close}>
      <aside ref={popup} id="minimap-popup" className="minimap-popup" role="dialog" aria-modal="true" aria-label={t('#Minimap')} onClick={(event) => event.stopPropagation()}>
        <header className="sheet-head">
          {levels.length > 1
            ? <FloorSelector key={state.projectId} levels={levels} value={state.levelIndex} onChange={index => store.setActiveLevel(index)} />
            : <h2>{levels.length ? levelLabel(levels, state.levelIndex) : t('#MinimapFloorPlan')}</h2>}
          <button className="icon-btn" aria-label={t('#MinimapClose')} onClick={close}><Icon name="close" size={18} /></button>
        </header>
        <div className="minimap-map">
          {rooms.length ? (
            <svg viewBox={`0 0 ${layout.width} ${layout.height}`} width={layout.width} height={layout.height} role="group" aria-label={t('#MinimapFloorPlan')}>
              {rooms.map((room) => {
                const hidden = isolated && state.isolatedRoom !== room.index;
                const c = toMinimap(labelCenter(room.polygon), layout);
                // A label wider than its room is noise: the app wraps and shrinks, the web hides.
                const xs = room.polygon.map((p) => p.x);
                const fits = (Math.max(...xs) - Math.min(...xs)) * layout.scale >= roomName(room).length * 6.5;
                return (
                  <g key={room.index} className="minimap-room" role="button" tabIndex={0} aria-label={roomName(room)} aria-current={room === active ? 'location' : undefined} data-room={room.index}
                    onClick={() => go(room)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); go(room); } }}>
                    <path d={pathOf(room, layout)} fill={room === active ? MINIMAP_ACTIVE_COLOR : MINIMAP_ROOM_COLOR} stroke={hidden ? MINIMAP_HIDDEN_BORDER_COLOR : MINIMAP_BORDER_COLOR} strokeWidth={wallStroke} strokeLinejoin="round" />
                    {!hidden && fits && <text className="minimap-label" x={c.x} y={c.y} textAnchor="middle" dominantBaseline="middle">{roomName(room).toUpperCase()}</text>}
                  </g>
                );
              })}
              {arrow && <CameraIndicator arrow={arrow} layout={layout} size={32} />}
            </svg>
          ) : <p className="render-note">{t('#MinimapNoRooms')}</p>}
        </div>
        <p className="minimap-hint">{rooms.length ? t('#MinimapHint') : t('#MinimapChooseFloor')}</p>
        <footer className="sheet-foot">
          <button className="minimap-hide" disabled={!rooms.length} aria-pressed={isolated} onClick={toggleIsolation}>{isolated ? t('#ShowAllRooms') : t('#MinimapHideOthers')}</button>
        </footer>
      </aside>
    </div>
  );
}
